import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Col, Modal, Row, Table } from "react-bootstrap";
import Select from "react-select";
import { useDispatch } from "react-redux";
import {
  CustomDropdownContainer,
  FormFieldLabel,
  PageHeader,
  StatusBadge,
  TablePagination,
  TableSearchField,
} from "CommonComponent";
import { hideCommonLoader, showCommonLoader } from "actions/commonActions";
import { useObjectViewedId } from "hooks/useObjectViewedId";
import {
  EVENT_CODE_OPTIONS,
  MAIL_LOG_STATUS_OPTIONS,
  QUEUE_MODE_OPTIONS,
  fetchEmailMailLog,
  fetchEmailMailLogs,
  getErrorMessage,
} from "../emailSettingsApi";
import "../EmailSettings.css";

const notify = (message, type = "general_error") => ({
  type: "SHOW_SNACKBAR",
  snackbar_notification: message,
  snackbar_notification_type: type,
});

const selectMenuProps = {
  menuPortalTarget: typeof document !== "undefined" ? document.body : null,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  },
};

const STATUS_VARIANT = {
  sent: "approved",
  failed: "rejected",
  queued: "pending",
};

const META_LABELS = {
  source: "Source",
  applied_stored_smtp: "Applied stored SMTP",
  legacy_event_key: "Legacy event key",
  intended_email: "Intended email",
  reminder_id: "Reminder ID",
};

const formatDateTime = (value) =>
  value ? String(value).replace("T", " ").slice(0, 19) : "—";

const shortId = (value) => (value ? String(value).slice(0, 8) : "—");

const queueModeLabel = (value) =>
  QUEUE_MODE_OPTIONS.find((o) => o.value === value)?.label || value || "—";

const statusLabel = (value) =>
  value ? value.charAt(0).toUpperCase() + value.slice(1) : "—";

const looksLikeHtml = (value) => {
  const text = String(value || "").trim();
  return (
    text.startsWith("<!DOCTYPE") ||
    text.startsWith("<html") ||
    /<\/?[a-z][\s\S]*>/i.test(text)
  );
};

const formatMetaValue = (value) => {
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

const DetailField = ({ label, children, className = "" }) => (
  <div className={`email-settings-detail-field ${className}`.trim()}>
    <div className="email-settings-detail-label">{label}</div>
    <div className="email-settings-detail-value">{children ?? "—"}</div>
  </div>
);

const EmptyValue = () => <span className="email-settings-empty">Not set</span>;

const EmailMailLogs = () => {
  const dispatch = useDispatch();
  const objectViewedId = useObjectViewedId();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);
  const [eventCode, setEventCode] = useState(null);
  const [status, setStatus] = useState(null);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [detail, setDetail] = useState(null);
  const [bodyTab, setBodyTab] = useState("preview");

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [eventCode, status, search, perPage]);

  const load = useCallback(async () => {
    dispatch(showCommonLoader());
    try {
      const result = await fetchEmailMailLogs(objectViewedId, {
        event_code: eventCode?.value,
        status: status?.value,
        search,
        page,
        per_page: perPage,
      });
      setRows(result.rows);
      setMeta(result.meta);
    } catch (error) {
      dispatch(notify(getErrorMessage(error)));
    } finally {
      dispatch(hideCommonLoader());
    }
  }, [dispatch, objectViewedId, eventCode, status, search, page, perPage]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetail = async (row) => {
    setDetail(row);
    setBodyTab("preview");
    try {
      const full = await fetchEmailMailLog(row.id, objectViewedId);
      if (full?.id) {
        setDetail(full);
      }
    } catch (error) {
      // Non-blocking — the list row already carries most of the detail.
    }
  };

  const closeDetail = () => {
    setDetail(null);
    setBodyTab("preview");
  };

  const bodyHtml = detail?.body_preview || "";
  const bodyIsHtml = useMemo(() => looksLikeHtml(bodyHtml), [bodyHtml]);
  const metaEntries = useMemo(() => {
    if (!detail?.meta || typeof detail.meta !== "object") return [];
    return Object.entries(detail.meta);
  }, [detail]);

  return (
    <div className="email-settings-page">
      <PageHeader title="Mail Logs">
        <Button variant="outline-secondary" size="sm" onClick={load}>
          Refresh
        </Button>
      </PageHeader>

      <div className="main-content-div page-card">
        <Row className="g-3 mb-3">
          <Col md={4}>
            <FormFieldLabel>Event</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={EVENT_CODE_OPTIONS}
              value={eventCode}
              onChange={setEventCode}
              isClearable
              placeholder="All events"
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
          <Col md={4}>
            <FormFieldLabel>Status</FormFieldLabel>
            <Select
              className="multi-select"
              classNamePrefix="react-select"
              options={MAIL_LOG_STATUS_OPTIONS}
              value={status}
              onChange={setStatus}
              isClearable
              placeholder="All statuses"
              components={{ ValueContainer: CustomDropdownContainer }}
              {...selectMenuProps}
            />
          </Col>
          <Col md={4}>
            <FormFieldLabel>Search</FormFieldLabel>
            <TableSearchField
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Recipient or subject..."
              maxWidth="100%"
            />
          </Col>
        </Row>

        <Table responsive>
          <thead>
            <tr>
              <th>Event</th>
              <th>Recipient</th>
              <th>Subject</th>
              <th>Status</th>
              <th>Queue Mode</th>
              <th>Related PAF</th>
              <th>Attempts</th>
              <th>Created</th>
              <th>Sent / Failed</th>
              <th className="text-end">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={10}>No mail logs found.</td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr
                  key={row.id}
                  className="email-settings-log-row"
                  onClick={() => openDetail(row)}
                >
                  <td>{row.event_label || row.event_code || "—"}</td>
                  <td>
                    {row.recipient_name
                      ? `${row.recipient_name} (${row.recipient_email})`
                      : row.recipient_email || "—"}
                  </td>
                  <td>{row.subject || "—"}</td>
                  <td>
                    <StatusBadge
                      label={statusLabel(row.status)}
                      variant={STATUS_VARIANT[row.status] || "pending"}
                    />
                  </td>
                  <td>{queueModeLabel(row.queue_mode)}</td>
                  <td
                    className="email-settings-mono"
                    title={row.appraisal_progress_id || ""}
                  >
                    {shortId(row.appraisal_progress_id)}
                  </td>
                  <td>{row.attempt_count ?? 0}</td>
                  <td>{formatDateTime(row.date_created)}</td>
                  <td>{formatDateTime(row.sent_at || row.failed_at)}</td>
                  <td className="form-action-width text-end">
                    <Button
                      variant="none"
                      className="border-0"
                      title="View"
                      onClick={(e) => {
                        e.stopPropagation();
                        openDetail(row);
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>

        {meta?.last_page > 1 ? (
          <TablePagination
            currentPage={meta.current_page || page}
            totalPages={meta.last_page || 1}
            pageSize={perPage}
            pageSizeOptions={[10, 25, 50, 100]}
            onPageChange={setPage}
            onPageSizeChange={setPerPage}
          />
        ) : null}
      </div>

      <Modal
        show={!!detail}
        onHide={closeDetail}
        size="xl"
        centered
        dialogClassName="email-settings-log-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Mail Log Detail</Modal.Title>
        </Modal.Header>
        <Modal.Body className="email-settings-detail">
          {detail ? (
            <>
              <div className="email-settings-detail-summary">
                <div>
                  <div className="email-settings-detail-summary-kicker">
                    Event
                  </div>
                  <div className="email-settings-detail-summary-title">
                    {detail.event_label || detail.event_code || "—"}
                  </div>
                  <div className="email-settings-detail-summary-sub">
                    {detail.event_code &&
                    detail.event_code !== detail.event_label
                      ? detail.event_code
                      : null}
                  </div>
                </div>
                <StatusBadge
                  label={statusLabel(detail.status)}
                  variant={STATUS_VARIANT[detail.status] || "pending"}
                />
              </div>

              <div className="email-settings-detail-grid">
                <DetailField label="Recipient">
                  {detail.recipient_name ? (
                    <>
                      <div>{detail.recipient_name}</div>
                      <div className="email-settings-detail-muted">
                        {detail.recipient_email}
                      </div>
                    </>
                  ) : (
                    detail.recipient_email || <EmptyValue />
                  )}
                </DetailField>
                <DetailField label="Subject">
                  {detail.subject || <EmptyValue />}
                </DetailField>
                <DetailField label="Template">
                  {detail.template_name || <EmptyValue />}
                </DetailField>
                <DetailField label="Queue Mode">
                  {queueModeLabel(detail.queue_mode)}
                </DetailField>
                <DetailField label="Attempts">
                  {detail.attempt_count ?? 0}
                </DetailField>
                <DetailField label="Related PAF">
                  {detail.appraisal_progress_id ? (
                    <span
                      className="email-settings-mono"
                      title={detail.appraisal_progress_id}
                    >
                      {detail.appraisal_progress_id}
                    </span>
                  ) : (
                    <EmptyValue />
                  )}
                </DetailField>
              </div>

              <div className="email-settings-detail-section-title">Timeline</div>
              <div className="email-settings-timeline">
                <DetailField label="Created">
                  {formatDateTime(detail.date_created)}
                </DetailField>
                <DetailField label="Queued">
                  {formatDateTime(detail.queued_at)}
                </DetailField>
                <DetailField label="Sent">
                  {formatDateTime(detail.sent_at)}
                </DetailField>
                <DetailField label="Failed">
                  {formatDateTime(detail.failed_at)}
                </DetailField>
              </div>

              {detail.error_message ? (
                <>
                  <div className="email-settings-detail-section-title">
                    Error
                  </div>
                  <div className="email-settings-error-box">
                    {detail.error_message}
                  </div>
                </>
              ) : null}

              <div className="email-settings-detail-section-title email-settings-body-header">
                <span>Email Body</span>
                {bodyHtml ? (
                  <div className="email-settings-body-tabs">
                    <button
                      type="button"
                      className={
                        bodyTab === "preview"
                          ? "email-settings-body-tab is-active"
                          : "email-settings-body-tab"
                      }
                      onClick={() => setBodyTab("preview")}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className={
                        bodyTab === "source"
                          ? "email-settings-body-tab is-active"
                          : "email-settings-body-tab"
                      }
                      onClick={() => setBodyTab("source")}
                    >
                      Source
                    </button>
                  </div>
                ) : null}
              </div>

              {!bodyHtml ? (
                <div className="email-settings-body-empty">
                  No body content was stored for this log.
                </div>
              ) : bodyTab === "preview" && bodyIsHtml ? (
                <div className="email-settings-body-frame-wrap">
                  <iframe
                    title="Email body preview"
                    className="email-settings-body-frame"
                    sandbox=""
                    srcDoc={bodyHtml}
                  />
                </div>
              ) : bodyTab === "preview" ? (
                <div className="email-settings-body-text">{bodyHtml}</div>
              ) : (
                <pre className="email-settings-body-source">{bodyHtml}</pre>
              )}

              {metaEntries.length > 0 ? (
                <>
                  <div className="email-settings-detail-section-title">
                    Meta
                  </div>
                  <div className="email-settings-meta-grid">
                    {metaEntries.map(([key, value]) => (
                      <DetailField
                        key={key}
                        label={META_LABELS[key] || key.replace(/_/g, " ")}
                      >
                        {formatMetaValue(value)}
                      </DetailField>
                    ))}
                  </div>
                </>
              ) : null}
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeDetail}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EmailMailLogs;
